import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Copy, Link as LinkIcon, Plus, Share2, Trash2 } from 'lucide-react';
import { api } from '../lib/api';

export type CollectionLink = {
  id: string;
  token: string;
  label: string | null;
  created_at: string;
  guest_count: number;
  people_count?: number;
};

const PUBLIC_WEB =
  (import.meta.env.VITE_PUBLIC_WEB_URL as string | undefined)?.replace(/\/$/, '') ||
  (typeof window !== 'undefined' ? window.location.origin : '');

export function guestCollectionUrl(token: string): string {
  return `${PUBLIC_WEB}/guest-collection/${token}`;
}

function shareText(eventName: string, token: string, label: string | null): string {
  const url = guestCollectionUrl(token);
  const who = label ? ` (${label})` : '';
  return `היי${who}! כאן מזינים את רשימת האורחים שלכם ל"${eventName}" — כל אורח שתוסיפו נכנס ישר לרשימת המוזמנים:\n${url}`;
}

export default function GuestLinksTab({
  eventId,
  eventName,
}: {
  eventId: string;
  eventName: string;
}) {
  const [links, setLinks] = useState<CollectionLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/events/${eventId}/guest-collection-links`);
      setLinks(Array.isArray(r.data.links) ? r.data.links : []);
    } catch {
      toast.error('לא ניתן לטעון קישורים');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function createLink(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/events/${eventId}/guest-collection-links`, {
        label: label.trim() || undefined,
      });
      const created = r.data as CollectionLink;
      setLinks((prev) => [
        { ...created, guest_count: 0, people_count: 0 },
        ...prev,
      ]);
      setLabel('');
      toast.success('הקישור נוצר');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'שגיאה ביצירת קישור');
    } finally {
      setBusy(false);
    }
  }

  async function copyLink(token: string) {
    try {
      await navigator.clipboard.writeText(guestCollectionUrl(token));
      toast.success('הקישור הועתק');
    } catch {
      toast.error('לא הצלחנו להעתיק');
    }
  }

  function shareWhatsApp(token: string, lbl: string | null) {
    const text = encodeURIComponent(shareText(eventName, token, lbl));
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  }

  async function shareNative(token: string, lbl: string | null) {
    const message = shareText(eventName, token, lbl);
    if (navigator.share) {
      try {
        await navigator.share({ title: eventName, text: message });
        return;
      } catch {
        /* cancelled */
      }
    }
    await copyLink(token);
  }

  async function deleteLink(id: string) {
    if (!window.confirm('למחוק את הקישור? אורחים שכבר הוזנו יישארו ברשימה.')) return;
    try {
      await api.delete(`/events/${eventId}/guest-collection-links/${id}`);
      setLinks((prev) => prev.filter((l) => l.id !== id));
      toast.success('הקישור נמחק');
    } catch {
      toast.error('לא ניתן למחוק');
    }
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="surface p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-2xl bg-sage-100 text-sage-700 grid place-items-center shrink-0">
            <LinkIcon size={18} aria-hidden />
          </div>
          <div>
            <h2 className="font-semibold text-ink text-[16px]">קישורים לשליחה למשפחה</h2>
            <p className="text-[13px] text-ink-mute mt-1 leading-relaxed">
              צרו קישור אישי לכל בן משפחה (אמא, אבא, חברים). הם מזינים אורחים — ואתם רואים ברשימה מי הזין כל אחד.
            </p>
          </div>
        </div>
        <ol className="grid sm:grid-cols-3 gap-2 text-[12px] text-ink-mute">
          {[
            'צרו קישור עם תווית (למשל «אמא»)',
            'שלחו בוואטסאפ או העתיקו',
            'הם מזינים — אתם רואים הכול ברשימת האורחים',
          ].map((step, i) => (
            <li key={step} className="rounded-xl bg-paper-mist/70 border border-line px-3 py-2.5">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-sage-600 text-white text-[11px] font-bold ml-1.5">
                {i + 1}
              </span>
              {step}
            </li>
          ))}
        </ol>
      </div>

      <form onSubmit={createLink} className="surface p-4 space-y-3">
        <h3 className="font-semibold text-ink text-[15px]">יצירת קישור חדש</h3>
        <p className="text-[12px] text-ink-faint">כל קישור ייחודי — רואים בדיוק מי נרשם דרכו</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder='תווית (למשל: "לאמא", "לחברים")'
            className="field flex-1"
            aria-label="תווית הקישור"
          />
          <button type="submit" disabled={busy} className="btn-primary">
            <Plus size={15} aria-hidden />
            {busy ? 'יוצר…' : 'צור קישור'}
          </button>
        </div>
      </form>

      <div className="surface overflow-hidden divide-y divide-line">
        {loading ? (
          <p className="px-4 py-8 text-center text-sm text-ink-faint">טוען קישורים…</p>
        ) : links.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-ink-faint">
            עדיין אין קישורים — צרו אחד ושלחו לבני המשפחה
          </p>
        ) : (
          links.map((link) => (
            <div key={link.id} className="px-4 py-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-ink truncate">{link.label || 'קישור ללא שם'}</p>
                  <p className="text-[12px] text-ink-faint mt-0.5 break-all" dir="ltr">
                    {guestCollectionUrl(link.token)}
                  </p>
                  <p className="text-[12px] text-sage-700 font-semibold mt-1">
                    {link.guest_count} הזמנות
                    {link.people_count ? ` · ${link.people_count} אנשים` : ''}
                    {' · '}
                    {new Date(link.created_at).toLocaleDateString('he-IL')}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-ghost !px-2 !py-1.5 text-red-700"
                  title="מחיקת קישור"
                  onClick={() => void deleteLink(link.id)}
                >
                  <Trash2 size={15} aria-hidden />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" className="btn-secondary" onClick={() => void copyLink(link.token)}>
                  <Copy size={14} aria-hidden />
                  העתקה
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => void shareNative(link.token, link.label)}
                >
                  <Share2 size={14} aria-hidden />
                  שיתוף
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => shareWhatsApp(link.token, link.label)}
                >
                  וואטסאפ
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
